import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import type { ContactResolutionStatus, SyncStatus } from '../types';
import { formatBytes, formatDateTime, formatNumber, formatRelative } from '../format';

interface Props {
  status: SyncStatus | null;
  onRunAll: () => void;
  /** Edit level on Sync: start syncs and retries. */
  canRun?: boolean;
}

export function SyncStatusPage({ status, onRunAll, canRun = true }: Props) {
  const [contacts, setContacts] = useState<ContactResolutionStatus | null>(null);
  const [busy, setBusy] = useState<'resolve' | 'retry' | null>(null);

  const refreshContacts = useCallback(() => {
    api.contactStatus().then(setContacts).catch(() => undefined);
  }, []);

  useEffect(refreshContacts, [refreshContacts]);

  async function resolveNow() {
    setBusy('resolve');
    try {
      await api.resolveContacts();
      refreshContacts();
    } finally {
      setBusy(null);
    }
  }

  async function retryMissing() {
    setBusy('retry');
    try {
      await api.retryMissingContacts();
      await api.resolveContacts();
      refreshContacts();
    } finally {
      setBusy(null);
    }
  }

  if (!status) return <p className="empty">Loading sync status…</p>;

  const summary = status.lastSummary;

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h2>Sync</h2>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onRunAll}
            disabled={!canRun || status.running || status.demoMode}
          >
            {status.running ? 'Syncing…' : 'Sync all sub-accounts'}
          </button>
        </div>
        <p className="card-sub">
          {status.demoMode
            ? 'Demo mode: syncing is off and these rows describe seeded data.'
            : status.enabled
            ? `New calls, activity and this month's appointments sync every ${status.intervalMinutes} minutes; ` +
              `a complete pass (users, calendars, all appointments, every opportunity) runs every ` +
              `${status.fullIntervalMinutes} minutes. Up to ${status.concurrency} sub-accounts sync at once.`
            : 'Background sync is disabled (SYNC_ENABLED=false) — pull manually.'}{' '}
          Reports on screen update by themselves as each sub-account finishes.
        </p>

        {status.progress ? (
          <p className="small">
            <strong>
              Syncing {formatNumber(Math.min(status.progress.done + 1, status.progress.total))} of{' '}
              {formatNumber(status.progress.total)} sub-accounts
            </strong>
            {status.enriching ? ' · filling in contact names and recordings' : ''}
          </p>
        ) : status.enriching ? (
          <p className="small">
            <strong>Data is up to date.</strong> Filling in contact names and copying recordings in the
            background.
          </p>
        ) : null}

        <div className="grid grid-kpi">
          <div className="stat">
            <span className="stat-label">Last run</span>
            <span className="stat-value">{summary ? formatRelative(summary.finishedAt) : '—'}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Calls pulled</span>
            <span className="stat-value">{summary ? formatNumber(summary.calls) : '—'}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Contacts resolved</span>
            <span className="stat-value">{summary ? formatNumber(summary.contacts) : '—'}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Appointments pulled</span>
            <span className="stat-value">{summary ? formatNumber(summary.appointments) : '—'}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Contacts queued</span>
            <span className="stat-value">{formatNumber(status.pendingContacts)}</span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Recording archive</h2>
          <span className="small muted">{formatBytes(status.recordings.bytes)} stored</span>
        </div>
        <p className="card-sub">
          Every recording is copied into the dashboard's database once, newest first, so it stays
          playable whatever happens to HighLevel's copy. Playing a recording that has not been
          copied yet copies it on the spot. Unanswered calls and calls under a minute are skipped.
          Browse, sort and delete stored recordings in the Files tab.
        </p>
        <div className="grid grid-kpi">
          <div className="stat">
            <span className="stat-label">Stored</span>
            <span className="stat-value">{formatNumber(status.recordings.stored)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Waiting to copy</span>
            <span className="stat-value">{formatNumber(status.recordings.waiting)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">No recording in HighLevel</span>
            <span className="stat-value">{formatNumber(status.recordings.missing)}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Retrying</span>
            <span className="stat-value">{formatNumber(status.recordings.failing)}</span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Contact names</h2>
          <div className="row">
            <button type="button" className="btn btn-sm" onClick={resolveNow} disabled={!canRun || busy !== null}>
              {busy === 'resolve' ? 'Resolving…' : 'Resolve now'}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={retryMissing}
              disabled={!canRun || busy !== null || !contacts?.counts.missing}
            >
              {busy === 'retry' ? 'Retrying…' : 'Retry given-up'}
            </button>
          </div>
        </div>
        <p className="card-sub">
          HighLevel has no bulk contact endpoint, so names are fetched one at a time and fill in
          over successive syncs. A call showing “Open contact” is one whose name has not resolved yet.
        </p>

        <div className="grid grid-kpi">
          <div className="stat">
            <span className="stat-label">Resolved</span>
            <span className="stat-value">{contacts ? formatNumber(contacts.counts.ok) : '—'}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Queued</span>
            <span className="stat-value">{contacts ? formatNumber(contacts.counts.pending) : '—'}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Given up</span>
            <span className="stat-value">{contacts ? formatNumber(contacts.counts.missing) : '—'}</span>
          </div>
        </div>

        {contacts && contacts.errors.length > 0 ? (
          <div className="table-wrap" style={{ marginTop: 14 }}>
            <table>
              <thead>
                <tr>
                  <th>Why a lookup failed</th>
                  <th className="num">Contacts</th>
                </tr>
              </thead>
              <tbody>
                {contacts.errors.map((entry) => (
                  <tr key={entry.last_error}>
                    <td className="small">{entry.last_error}</td>
                    <td className="num">{formatNumber(entry.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {summary && summary.skipped.length > 0 ? (
        <div className="card">
          <h2>Skipped — no credentials</h2>
          <p className="card-sub">
            These sub-accounts have no Private Integration Token and no marketplace install able to
            mint one, so syncing passes over them. Connect the marketplace app or add a token under <strong>Sub-accounts</strong>.
          </p>
          <div className="chips">
            {summary.skipped.map((entry) => (
              <span className="chip" key={entry.locationId}>
                {entry.name ?? entry.locationId}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {summary && summary.errors.length > 0 ? (
        <div className="card">
          <h2>Errors from the last run</h2>
          <p className="card-sub">These sub-accounts did not sync cleanly.</p>
          <div className="stack">
            {summary.errors.map((error) => (
              <div className="banner error" key={`${error.locationId}-${error.message}`}>
                <span>
                  <strong>{error.locationId}</strong> — {error.message}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="card">
        <h2>Per sub-account</h2>
        <p className="card-sub">Where each sub-account's call history has been read up to.</p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Sub-account</th>
                <th>Status</th>
                <th>Last success</th>
                <th>Newest call</th>
                <th className="num">Calls stored</th>
                <th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {status.perLocation.map((row) => (
                <tr key={row.locationId}>
                  <td className="cell-strong">{row.locationName ?? row.locationId}</td>
                  <td style={row.lastStatus === 'error' ? { color: 'var(--danger-text)' } : undefined}>
                    {row.lastStatus ?? '—'}
                  </td>
                  <td className="nowrap">{formatRelative(row.lastSuccessAt)}</td>
                  <td className="nowrap">{formatDateTime(row.lastCallAt)}</td>
                  <td className="num">{formatNumber(row.callsStored)}</td>
                  <td className="small muted">{row.lastError ?? '—'}</td>
                </tr>
              ))}
              {status.perLocation.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty">
                    Nothing synced yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
